import React, { useEffect, useMemo, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

const AGENT_INFO = {
  trigger: {
    summary: "Identifierar och strukturerar nya triggers från inkommande signaler.",
    purpose: "Skapa högkvalitativa trigger-objekt som kan processas vidare i pipelinen.",
  },
  "trigger-scout": {
    summary: "Gör första screening av triggerns styrka, relevans och prioritet.",
    purpose: "Sortera ut svaga signaler och lyfta lovande triggers till fortsatt analys.",
  },
  "trigger-dedupe-judge": {
    summary: "Jämför nya triggers mot historik och avgör dedupe/novelty.",
    purpose: "Minska dubletter och styra tvetydiga fall till manuell novelty-review.",
  },
  "company-ranker": {
    summary: "Rangar shortlistade bolag mot triggerns köplogik och behov.",
    purpose: "Prioritera vilka bolag som ska verifieras först.",
  },
  "company-verify": {
    summary: "Verifierar bolagsmatchning med källor och reducerar brus.",
    purpose: "Behålla bolag med verklig passform och kasta bort svaga matchningar.",
  },
  "company-analyst": {
    summary: "Analyserar verifierade bolag ur PE-perspektiv.",
    purpose: "Bedöma investeringsrelevans och kvalitet inför IC-steg.",
  },
  "ic-analyst": {
    summary: "Skapar IC-vinklad analys och rekommendation.",
    purpose: "Formulera beslutsunderlag för investeringskommitté.",
  },
  "ic-rebuttal": {
    summary: "Utmanar IC-analysen med motargument och riskperspektiv.",
    purpose: "Öka robustheten genom att identifiera svagheter i huvudtesen.",
  },
  "red-team": {
    summary: "Gör adversarial granskning av analysens antaganden.",
    purpose: "Minska bias och felbeslut genom aktiv stress-testning.",
  },
  "final-ic-decision": {
    summary: "Konsoliderar analys, rebuttal och red-team till slutlig linje.",
    purpose: "Leverera tydlig slutrekommendation med spårbar motivering.",
  },
};

const PIPELINE_AGENT_ORDER = [
  "trigger",
  "trigger-scout",
  "trigger-dedupe-judge",
  "company-ranker",
  "company-verify",
  "company-analyst",
  "ic-analyst",
  "ic-rebuttal",
  "red-team",
  "final-ic-decision",
];

const PIPELINE_FLOW_STEPS = [
  {
    id: "cleanup",
    type: "system",
    title: "Initial cleanup",
    description: "Rensar gamla rapporter, loggar och äldre sessionfiler innan körning.",
  },
  {
    id: "trigger",
    type: "agent",
    title: "Trigger-agent",
    description: "Skannar nya signaler och producerar trigger-JSON för vidare process.",
  },
  {
    id: "wait-for-trigger",
    type: "system",
    title: "Vänta på trigger-resultat",
    description: "Inväntar ny sessionfil med trigger-output innan nästa steg.",
  },
  {
    id: "download-company-universe",
    type: "system",
    title: "Ladda bolagsuniversum",
    description: "Hämtar extern bolagsdata som används i matchning och ranking.",
  },
  {
    id: "import-company-universe",
    type: "system",
    title: "Importera bolagsuniversum",
    description: "Materialiserar bolagsdata till lokal databas för pipelinekörningen.",
  },
  {
    id: "extract-triggers",
    type: "system",
    title: "Extrahera triggers",
    description: "Plockar ut och normaliserar triggerobjekt från sessionsfiler.",
  },
  {
    id: "sync-trigger-workflow",
    type: "system",
    title: "Synka workflow",
    description: "Synkroniserar triggerstatus till arbetsflödestabeller och metadata.",
  },
  {
    id: "resolve-trigger-novelty",
    type: "system",
    title: "Novelty-bedömning",
    description: "Kör novelty-resolver som orkestrerar dedupe-beslut och statusuppdatering.",
  },
  {
    id: "trigger-dedupe-judge",
    type: "agent",
    title: "Trigger-dedupe-judge",
    description: "Jämför NEW vs EXISTING trigger och avgör new/duplicate/ambiguous.",
  },
  {
    id: "trigger-scout",
    type: "agent",
    title: "Trigger-scout",
    description: "Gör första kvalitets- och relevansscreening av varje trigger.",
  },
  {
    id: "build-company-shortlist",
    type: "system",
    title: "Bygg shortlist",
    description: "Skapar kandidatlista av bolag som går vidare till rankning.",
  },
  {
    id: "company-ranker",
    type: "agent",
    title: "Company-ranker",
    description: "Rankar shortlistade bolag mot triggerns köplogik och prioritet.",
  },
  {
    id: "company-verify",
    type: "agent",
    title: "Company-verify",
    description: "Verifierar datakvalitet och passform innan analyssteg.",
  },
  {
    id: "materialize-ic-cases",
    type: "system",
    title: "Skapa IC-cases",
    description: "Materialiserar verifierade kandidater till strukturerade IC-cases.",
  },
  {
    id: "update-trigger-status",
    type: "system",
    title: "Uppdatera triggerstatus",
    description: "Markerar triggers med korrekt status efter case-materialisering.",
  },
  {
    id: "company-analyst",
    type: "agent",
    title: "Company-analyst",
    description: "Skapar investeringsanalys per case inför IC-beslutskedjan.",
  },
  {
    id: "ic-analyst",
    type: "agent",
    title: "IC-analyst",
    description: "Producerar IC-orienterat underlag och rekommendationsförslag.",
  },
  {
    id: "red-team-loop",
    type: "system",
    title: "Red-team-loop",
    description: "Itererar red-team-processen tills inga case återstår i kön.",
  },
  {
    id: "red-team",
    type: "agent",
    title: "Red-team",
    description: "Stress-testar antaganden och lyfter risker i huvudtesen.",
  },
  {
    id: "rebuttal-loop",
    type: "system",
    title: "Rebuttal-loop",
    description: "Kör rebuttal-rundor tills alla red-teamade case är hanterade.",
  },
  {
    id: "ic-rebuttal",
    type: "agent",
    title: "IC-rebuttal",
    description: "Svarar på invändningar och förfinar analysens beslutsunderlag.",
  },
  {
    id: "final-ic-loop",
    type: "system",
    title: "Final IC-loop",
    description: "Kör slutrundor tills alla rebuttal-klara case har beslut.",
  },
  {
    id: "final-ic-decision",
    type: "agent",
    title: "Final-ic-decision",
    description: "Sätter slutlig IC-rekommendation med tydlig motivering.",
  },
  {
    id: "report",
    type: "system",
    title: "Pipeline-rapport",
    description: "Genererar sammanfattande rapport för run-id och utfall.",
  },
  {
    id: "notify",
    type: "system",
    title: "Notifiering",
    description: "Skapar/sänder sammanfattning för nya triggers och körningsutfall.",
  },
];

function getAgentInfo(agentId) {
  return AGENT_INFO[agentId] || {
    summary: "Agent i projektets pipeline.",
    purpose: "Bidra med specialiserat beslutstöd i flödet.",
  };
}

function formatVersionTimestamp(raw) {
  const text = String(raw || "").trim();
  if (!text) return "okänd tid";
  // SQLite datetime('now') -> UTC string without timezone marker.
  const normalized = text.includes("T") ? text : `${text.replace(" ", "T")}Z`;
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return text;
  return dt.toLocaleString("sv-SE");
}

export default function AdminAgentsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [agentState, setAgentState] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [modelCatalog, setModelCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogFetchedAt, setCatalogFetchedAt] = useState("");

  const setStateFor = (agentId, patch) => {
    setAgentState((prev) => ({
      ...prev,
      [agentId]: {
        ...(prev[agentId] || {}),
        ...patch,
      },
    }));
  };

  const loadAgents = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${getApiBase()}/admin/openclaw/agents`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setAgents(rows);
      })
      .catch((e) => setError(e.message || "Kunde inte läsa agenter"))
      .finally(() => setLoading(false));
  };

  const loadAgentDetails = (agentId, { force = false } = {}) => {
    const current = agentState[agentId];
    if (!force && current?.loaded) return;
    setStateFor(agentId, { loading: true, error: null, ok: null });
    Promise.all([
      apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/docs`).then((r) => parseJsonOrExplain(r)),
      apiFetch(`${getApiBase()}/admin/learning/summary?agent_id=${encodeURIComponent(agentId)}`).then((r) => parseJsonOrExplain(r)),
    ])
      .then(([docs, learning]) => {
        setStateFor(agentId, {
          loading: false,
          loaded: true,
          agentsMd: String(docs?.agents_md || ""),
          soulMd: String(docs?.soul_md || ""),
          memoryMd: String(docs?.memory_md || ""),
          learningSummary: learning || null,
          error: null,
        });
      })
      .catch((e) => {
        setStateFor(agentId, {
          loading: false,
          error: e.message || "Kunde inte läsa agentdata",
        });
      });
  };

  const saveAgentDocs = (agentId) => {
    const st = agentState[agentId] || {};
    setStateFor(agentId, { docsSaving: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/docs`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        agents_md: st.agentsMd || "",
        soul_md: st.soulMd || "",
        memory_md: st.memoryMd || "",
      }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setStateFor(agentId, { ok: `Sparat för ${agentId}.` });
      })
      .catch((e) => {
        setStateFor(agentId, { error: e.message || "Kunde inte spara agentdokument" });
      })
      .finally(() => setStateFor(agentId, { docsSaving: false }));
  };

  const loadModelCatalog = (refresh = false) => {
    setCatalogLoading(true);
    setCatalogError(null);
    const q = refresh ? "?refresh=true" : "";
    apiFetch(`${getApiBase()}/admin/openclaw/models/catalog${q}`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        setModelCatalog(Array.isArray(data?.providers) ? data.providers : []);
        setCatalogFetchedAt(String(data?.fetched_at || ""));
      })
      .catch((e) => setCatalogError(e.message || "Kunde inte läsa modellkatalog"))
      .finally(() => setCatalogLoading(false));
  };

  const loadAgentModel = (agentId) => {
    setStateFor(agentId, { modelLoading: true, error: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/model`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const allModels = Array.isArray(data?.all_models) ? data.all_models : [];
        const allowedModels = Array.isArray(data?.allowed_models) ? data.allowed_models : [];
        const merged = Array.from(new Set([...allowedModels, ...allModels]));
        setStateFor(agentId, {
          modelLoading: false,
          currentModel: String(data?.current_model || ""),
          selectedModel: String(data?.current_model || ""),
          modelOptions: merged,
        });
      })
      .catch((e) => {
        setStateFor(agentId, { modelLoading: false, error: e.message || "Kunde inte läsa agentmodell" });
      });
  };

  const saveAgentModel = (agentId) => {
    const st = agentState[agentId] || {};
    const model = String(st.selectedModel || "").trim();
    if (!model) {
      setStateFor(agentId, { error: "Välj en modell först." });
      return;
    }
    setStateFor(agentId, { modelSaving: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/model`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        setStateFor(agentId, {
          currentModel: String(data?.current_model || model),
          selectedModel: String(data?.current_model || model),
          ok: `Modell uppdaterad för ${agentId}.`,
        });
      })
      .catch((e) => {
        setStateFor(agentId, { error: e.message || "Kunde inte uppdatera agentmodell" });
      })
      .finally(() => setStateFor(agentId, { modelSaving: false }));
  };

  const loadVersionHistory = (agentId) => {
    setStateFor(agentId, { historyLoading: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/docs/history`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const versions = Array.isArray(data?.versions) ? data.versions : [];
        const selectedVersionId = versions.length > 0 ? Number(versions[0].id) : null;
        setStateFor(agentId, {
          historyLoading: false,
          versions,
          selectedVersionId,
        });
      })
      .catch((e) => {
        setStateFor(agentId, {
          historyLoading: false,
          error: e.message || "Kunde inte läsa versionshistorik",
        });
      });
  };

  const restoreSelectedVersion = (agentId) => {
    const st = agentState[agentId] || {};
    const versionId = Number(st.selectedVersionId);
    if (!Number.isInteger(versionId) || versionId <= 0) {
      setStateFor(agentId, { error: "Välj en version att återställa." });
      return;
    }
    setStateFor(agentId, { restoreBusy: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/docs/restore`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version_id: versionId }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const docType = String(data?.doc_type || "").toUpperCase();
        setStateFor(agentId, {
          ok: `${docType || "Dokument"} återställd från version #${versionId}.`,
        });
        loadAgentDetails(agentId, { force: true });
        loadVersionHistory(agentId);
      })
      .catch((e) => {
        setStateFor(agentId, { error: e.message || "Kunde inte återställa version" });
      })
      .finally(() => setStateFor(agentId, { restoreBusy: false }));
  };

  const saveMemoryOnly = (agentId) => {
    const st = agentState[agentId] || {};
    setStateFor(agentId, { memorySaving: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/memory`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        memory_md: st.memoryMd || "",
      }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setStateFor(agentId, { ok: `MEMORY.md sparad för ${agentId}.` });
      })
      .catch((e) => {
        setStateFor(agentId, { error: e.message || "Kunde inte spara MEMORY.md" });
      })
      .finally(() => setStateFor(agentId, { memorySaving: false }));
  };

  const generateMemoryDraft = (agentId) => {
    setStateFor(agentId, { memoryDraftBusy: true, error: null, ok: null });
    apiFetch(`${getApiBase()}/admin/openclaw/agents/${encodeURIComponent(agentId)}/memory/draft`, {
      method: "POST",
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        const mode = String(data?.generation_mode || "rule_based");
        const semanticErr = String(data?.semantic_error || "").trim();
        let ok = `Regelbaserat utkast från ${Number(data?.based_on_events || 0)} events.`;
        if (mode === "semantic_ai") {
          ok = `Semantiskt AI-utkast genererat från ${Number(data?.based_on_events || 0)} events.`;
        } else if (mode === "rule_based_fallback" && semanticErr) {
          ok = `AI-utkast misslyckades (${semanticErr}), fallback användes.`;
        }
        setStateFor(agentId, {
          memoryMd: String(data?.memory_draft || ""),
          ok,
        });
      })
      .catch((e) => {
        setStateFor(agentId, { error: e.message || "Kunde inte generera MEMORY-utkast" });
      })
      .finally(() => setStateFor(agentId, { memoryDraftBusy: false }));
  };

  const refreshAllMemory = () => {
    setRefreshingAll(true);
    setRefreshError(null);
    setRefreshResult(null);
    apiFetch(`${getApiBase()}/admin/openclaw/agents/memory/refresh-all`, {
      method: "POST",
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        setRefreshResult(data || null);
        // Om en öppen agent påverkades, ladda om dess data så textrutan visar sparat MEMORY.md.
        const updatedIds = new Set((data?.updated || []).map((u) => String(u.agent_id)));
        if (activeAgentId && updatedIds.has(String(activeAgentId))) {
          loadAgentDetails(activeAgentId, { force: true });
        }
      })
      .catch((e) => setRefreshError(e.message || "Kunde inte uppdatera MEMORY.md för agenter"))
      .finally(() => setRefreshingAll(false));
  };

  useEffect(() => {
    loadAgents();
    loadModelCatalog();
  }, []);

  const openAgentEditor = (agentId) => {
    const id = String(agentId || "");
    if (!id) return;
    setActiveAgentId(id);
    loadAgentDetails(id);
    loadAgentModel(id);
    loadVersionHistory(id);
  };

  const sortedAgents = useMemo(
    () => {
      const orderIndex = new Map(PIPELINE_AGENT_ORDER.map((id, idx) => [id, idx]));
      return [...agents].sort((a, b) => {
        const aId = String(a.id || "");
        const bId = String(b.id || "");
        const ai = orderIndex.has(aId) ? orderIndex.get(aId) : Number.MAX_SAFE_INTEGER;
        const bi = orderIndex.has(bId) ? orderIndex.get(bId) : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return aId.localeCompare(bId, "sv");
      });
    },
    [agents]
  );
  const agentsById = useMemo(
    () => new Map(sortedAgents.map((a) => [String(a.id || ""), a])),
    [sortedAgents]
  );
  const flowSteps = useMemo(
    () =>
      PIPELINE_FLOW_STEPS.map((step) => {
        const agent = step.type === "agent" ? agentsById.get(step.id) : null;
        return {
          ...step,
          agent,
        };
      }),
    [agentsById]
  );
  const activeAgent = useMemo(
    () => sortedAgents.find((a) => String(a.id || "") === String(activeAgentId || "")) || null,
    [sortedAgents, activeAgentId]
  );
  const activeState = activeAgent ? agentState[activeAgent.id] || {} : {};

  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: "30px", fontWeight: 500 }}>Admin - agenter</h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={refreshingAll}
            onClick={refreshAllMemory}
            title="Genererar och sparar nytt semantiskt MEMORY.md för alla agenter som fått ny input sedan senaste uppdatering."
            style={{
              padding: "8px 12px",
              borderRadius: "999px",
              border: "1px solid #111",
              background: "#111",
              color: "#fff",
              cursor: refreshingAll ? "wait" : "pointer",
            }}
          >
            {refreshingAll ? "Uppdaterar MEMORY.md..." : "Uppdatera alla MEMORY.md (ny input)"}
          </button>
          <button
            type="button"
            onClick={loadAgents}
            style={{
              padding: "8px 12px",
              borderRadius: "999px",
              border: "1px solid rgba(17,17,17,0.2)",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Ladda om agenter
          </button>
        </div>
      </div>

      {refreshError && (
        <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{refreshError}</p>
      )}
      {refreshingAll && (
        <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif", margin: "4px 0" }}>
          Går igenom alla agenter och genererar semantiska MEMORY.md-utkast. Detta kan ta en stund...
        </p>
      )}
      {refreshResult && (
        <div
          style={{
            border: "1px solid rgba(17,17,17,0.12)",
            borderRadius: "12px",
            padding: "12px",
            background: "#fff",
            marginTop: "6px",
            marginBottom: "12px",
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: "13px",
            color: "#3f3a34",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "6px" }}>
            Uppdaterade {refreshResult.updated_count || 0} av {refreshResult.total_agents || 0} agenter
            {" · "}
            {refreshResult.skipped_count || 0} hoppade över
            {(refreshResult.failed_count || 0) > 0 && (
              <span style={{ color: "#b42318" }}>{" · "}{refreshResult.failed_count} misslyckades</span>
            )}
          </div>
          {(refreshResult.updated || []).length > 0 && (
            <div style={{ marginBottom: "4px" }}>
              <strong>Uppdaterade:</strong>{" "}
              {refreshResult.updated
                .map((u) => `${u.agent_id} (${u.based_on_events} events)`)
                .join(", ")}
            </div>
          )}
          {(refreshResult.failed || []).length > 0 && (
            <div style={{ color: "#b42318" }}>
              <strong>Misslyckades:</strong>{" "}
              {refreshResult.failed.map((f) => `${f.agent_id}: ${f.error}`).join(" · ")}
            </div>
          )}
          {(refreshResult.updated || []).length === 0 &&
            (refreshResult.failed || []).length === 0 && (
              <div style={{ color: "#5f5a52" }}>
                Inga agenter hade ny input – inget behövde uppdateras.
              </div>
            )}
        </div>
      )}
      <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
        Öppna en agent i popup för att redigera AGENTS.md, SOUL.md, MEMORY.md, modell och versionshistorik.
      </p>
      <div
        style={{
          border: "1px solid rgba(17,17,17,0.12)",
          borderRadius: "14px",
          padding: "14px",
          background: "#fff",
          marginBottom: "14px",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "6px" }}>Pipelineflöde (agent + systemsteg)</div>
        <div style={{ color: "#5f5a52", fontSize: "13px", marginBottom: "10px" }}>
          Visar hela körordningen så det blir tydligt vad som sker mellan varje agentsteg.
        </div>
        <div style={{ display: "grid", gap: "8px" }}>
          {flowSteps.map((step, idx) => {
            const isAgent = step.type === "agent";
            const agentId = step.id;
            const canOpenAgent = isAgent && step.agent;
            return (
              <div
                key={step.id}
                style={{
                  border: "1px solid rgba(17,17,17,0.10)",
                  borderRadius: "10px",
                  padding: "10px",
                  background: isAgent ? "#fcfbf8" : "#f8fafc",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "12px", color: "#7a746c" }}>Steg {idx + 1}</span>
                    <span
                      style={{
                        fontSize: "11px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        borderRadius: "999px",
                        padding: "2px 8px",
                        background: "#fff",
                      }}
                    >
                      {isAgent ? "Agent" : "System"}
                    </span>
                    <strong style={{ fontSize: "14px" }}>{step.title}</strong>
                  </div>
                  {isAgent && (
                    <button
                      type="button"
                      disabled={!canOpenAgent}
                      onClick={() => {
                        if (!canOpenAgent) return;
                        openAgentEditor(agentId);
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        cursor: canOpenAgent ? "pointer" : "not-allowed",
                        fontSize: "12px",
                        opacity: canOpenAgent ? 1 : 0.6,
                      }}
                    >
                      {canOpenAgent ? "Öppna agent" : "Agent saknas"}
                    </button>
                  )}
                </div>
                <div style={{ marginTop: "4px", fontSize: "13px", color: "#3f3a34" }}>{step.description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {error && <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{error}</p>}

      {loading && (
        <p style={{ margin: 0, color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>Laddar agenter...</p>
      )}
      {!loading && sortedAgents.length === 0 && (
        <p style={{ margin: 0, color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>Inga agenter hittades.</p>
      )}
      {activeAgent && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveAgentId(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            zIndex: 1200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1080px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: "14px",
              border: "1px solid rgba(17,17,17,0.14)",
              background: "#fff",
              padding: "14px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
              <div>
                <div style={{ fontSize: "22px", fontWeight: 600 }}>{activeAgent.id}</div>
                <div style={{ marginTop: "4px", fontSize: "13px", color: "#5f5a52" }}>
                  {getAgentInfo(activeAgent.id).summary}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveAgentId(null)}
                style={{
                  padding: "7px 11px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.2)",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Stäng
              </button>
            </div>

            {activeState.error && <p style={{ color: "#b42318", marginTop: 0 }}>{activeState.error}</p>}
            {activeState.ok && <p style={{ color: "#0a7f5a", marginTop: 0 }}>{activeState.ok}</p>}
            {activeState.loading ? (
              <p style={{ margin: 0, color: "#5f5a52" }}>Laddar agentdata...</p>
            ) : (
              <>
                <div style={{ marginBottom: "12px", padding: "10px", border: "1px solid rgba(17,17,17,0.12)", borderRadius: "10px", background: "#fcfbf8" }}>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>Modell per steg</div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    {(() => {
                      const catalogValues = new Set();
                      modelCatalog.forEach((g) =>
                        (g.models || []).forEach((m) => catalogValues.add(String(m.value)))
                      );
                      const seen = new Set(catalogValues);
                      const extras = [];
                      (activeState.modelOptions || []).forEach((v) => {
                        const val = String(v);
                        if (!seen.has(val)) {
                          seen.add(val);
                          extras.push(val);
                        }
                      });
                      const cur = String(activeState.selectedModel || "");
                      if (cur && !seen.has(cur)) {
                        seen.add(cur);
                        extras.push(cur);
                      }
                      return (
                        <select
                          value={activeState.selectedModel || ""}
                          onChange={(e) => setStateFor(activeAgent.id, { selectedModel: e.target.value })}
                          disabled={Boolean(activeState.modelLoading) || Boolean(activeState.modelSaving)}
                          style={{
                            minWidth: "280px",
                            maxWidth: "100%",
                            padding: "8px 10px",
                            borderRadius: "8px",
                            border: "1px solid rgba(17,17,17,0.2)",
                            background: "#fff",
                            fontSize: "12px",
                          }}
                        >
                          <option value="">
                            {catalogLoading ? "Laddar modeller..." : "Välj modell"}
                          </option>
                          {modelCatalog.map((g) => (
                            <optgroup key={g.provider} label={g.label}>
                              {(g.models || []).map((m) => (
                                <option key={m.value} value={m.value} title={m.name || m.label}>
                                  {m.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                          {extras.length > 0 && (
                            <optgroup label="Övriga (konfig)">
                              {extras.map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      );
                    })()}
                    <button
                      type="button"
                      disabled={Boolean(activeState.modelSaving) || Boolean(activeState.modelLoading) || !activeState.selectedModel}
                      onClick={() => saveAgentModel(activeAgent.id)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "999px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        cursor: activeState.modelSaving ? "wait" : "pointer",
                      }}
                    >
                      {activeState.modelSaving ? "Sparar modell..." : "Spara modell"}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(activeState.modelLoading) || Boolean(activeState.modelSaving)}
                      onClick={() => loadAgentModel(activeAgent.id)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "999px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        cursor: activeState.modelLoading ? "wait" : "pointer",
                      }}
                    >
                      {activeState.modelLoading ? "Laddar..." : "Ladda modell"}
                    </button>
                    <button
                      type="button"
                      disabled={catalogLoading}
                      onClick={() => loadModelCatalog(true)}
                      title="Kör om provider-scanningen mot OpenAI och Anthropic. Kan ta upp till ett par minuter."
                      style={{
                        padding: "8px 12px",
                        borderRadius: "999px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        cursor: catalogLoading ? "wait" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {catalogLoading ? "Uppdaterar lista..." : "Uppdatera modell-lista"}
                    </button>
                  </div>
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#5f5a52" }}>
                    Aktiv modell: {activeState.currentModel || "okänd"}
                  </div>
                  {catalogError && (
                    <div style={{ marginTop: "4px", fontSize: "12px", color: "#b42318" }}>
                      Modellkatalog: {catalogError}
                    </div>
                  )}
                  <div style={{ marginTop: "4px", fontSize: "12px", color: "#7a746c" }}>
                    {modelCatalog.reduce((sum, g) => sum + (g.models ? g.models.length : 0), 0)} modeller
                    tillgängliga från {modelCatalog.map((g) => g.label).join(" + ") || "OpenAI + Anthropic"}
                    {catalogFetchedAt ? ` · uppdaterad ${formatVersionTimestamp(catalogFetchedAt)}` : ""}
                  </div>
                </div>

                <div style={{ marginBottom: "12px", padding: "10px", border: "1px solid rgba(17,17,17,0.12)", borderRadius: "10px", background: "#fcfbf8" }}>
                  <div style={{ fontWeight: 600, marginBottom: "6px" }}>Learning feedback</div>
                  {!activeState.learningSummary ? (
                    <div style={{ color: "#5f5a52", fontSize: "13px" }}>Ingen sammanfattning tillgänglig ännu.</div>
                  ) : (
                    <div style={{ fontSize: "13px", color: "#3f3a34" }}>
                      <div>Total events: {activeState.learningSummary.total || 0}</div>
                      <div>
                        Beslut:{" "}
                        {(activeState.learningSummary.by_decision || []).map((r) => `${r.decision}: ${r.total}`).join(" · ") || "–"}
                      </div>
                      <div>
                        Entitet:{" "}
                        {(activeState.learningSummary.by_entity_type || []).map((r) => `${r.entity_type}: ${r.total}`).join(" · ") || "–"}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "12px", padding: "10px", border: "1px solid rgba(17,17,17,0.12)", borderRadius: "10px", background: "#fcfbf8" }}>
                  <div style={{ fontWeight: 600, marginBottom: "8px" }}>Versionshistorik</div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "8px" }}>
                    <select
                      value={activeState.historyDocType || "all"}
                      onChange={(e) => setStateFor(activeAgent.id, { historyDocType: e.target.value, selectedVersionId: null })}
                      style={{
                        padding: "7px 9px",
                        borderRadius: "8px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        fontSize: "12px",
                      }}
                    >
                      <option value="all">Alla dokument</option>
                      <option value="agents">AGENTS.md</option>
                      <option value="soul">SOUL.md</option>
                      <option value="memory">MEMORY.md</option>
                    </select>
                    <button
                      type="button"
                      disabled={Boolean(activeState.historyLoading)}
                      onClick={() => loadVersionHistory(activeAgent.id)}
                      style={{
                        padding: "7px 10px",
                        borderRadius: "999px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        cursor: activeState.historyLoading ? "wait" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {activeState.historyLoading ? "Laddar historik..." : "Ladda historik"}
                    </button>
                  </div>
                  {(() => {
                    const versions = Array.isArray(activeState.versions) ? activeState.versions : [];
                    const filter = String(activeState.historyDocType || "all");
                    const filtered = versions.filter((v) => filter === "all" || String(v.doc_type) === filter);
                    return (
                      <>
                        <select
                          value={activeState.selectedVersionId || ""}
                          onChange={(e) => setStateFor(activeAgent.id, { selectedVersionId: Number(e.target.value) || null })}
                          style={{
                            width: "100%",
                            padding: "7px 9px",
                            borderRadius: "8px",
                            border: "1px solid rgba(17,17,17,0.2)",
                            background: "#fff",
                            fontSize: "12px",
                            marginBottom: "8px",
                          }}
                        >
                          <option value="">Välj version att återställa</option>
                          {filtered.map((v) => (
                            <option key={v.id} value={v.id}>
                              #{v.id} · {String(v.doc_type || "").toUpperCase()} · {formatVersionTimestamp(v.created_at)}
                              {v.is_restore ? " · restore" : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={Boolean(activeState.restoreBusy) || !activeState.selectedVersionId}
                          onClick={() => restoreSelectedVersion(activeAgent.id)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "999px",
                            border: "1px solid rgba(17,17,17,0.2)",
                            background: "#fff",
                            cursor: activeState.restoreBusy ? "wait" : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          {activeState.restoreBusy ? "Återställer..." : "Återställ vald version"}
                        </button>
                        <div style={{ marginTop: "6px", fontSize: "12px", color: "#5f5a52" }}>
                          {filtered.length === 0
                            ? "Ingen historik för valt filter ännu."
                            : `${filtered.length} versioner tillgängliga.`}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <label style={{ display: "block", fontSize: "13px", marginBottom: "5px" }}>AGENTS.md</label>
                <textarea
                  rows={12}
                  value={activeState.agentsMd || ""}
                  onChange={(e) => setStateFor(activeAgent.id, { agentsMd: e.target.value })}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    marginBottom: "10px",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid rgba(17,17,17,0.2)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "12px",
                  }}
                />

                <label style={{ display: "block", fontSize: "13px", marginBottom: "5px" }}>SOUL.md</label>
                <textarea
                  rows={8}
                  value={activeState.soulMd || ""}
                  onChange={(e) => setStateFor(activeAgent.id, { soulMd: e.target.value })}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    marginBottom: "10px",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid rgba(17,17,17,0.2)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "12px",
                  }}
                />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                  <label style={{ display: "block", fontSize: "13px", margin: 0 }}>MEMORY.md</label>
                  <button
                    type="button"
                    disabled={Boolean(activeState.memoryDraftBusy)}
                    onClick={() => generateMemoryDraft(activeAgent.id)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: "999px",
                      border: "1px solid rgba(17,17,17,0.2)",
                      background: "#fff",
                      cursor: activeState.memoryDraftBusy ? "wait" : "pointer",
                      fontSize: "12px",
                    }}
                  >
                    {activeState.memoryDraftBusy ? "Genererar..." : "Generera semantiskt utkast"}
                  </button>
                </div>
                <textarea
                  rows={12}
                  value={activeState.memoryMd || ""}
                  onChange={(e) => setStateFor(activeAgent.id, { memoryMd: e.target.value })}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    marginBottom: "10px",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid rgba(17,17,17,0.2)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "12px",
                  }}
                />

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={Boolean(activeState.memorySaving)}
                    onClick={() => saveMemoryOnly(activeAgent.id)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: "1px solid rgba(17,17,17,0.2)",
                      background: "#fff",
                      cursor: activeState.memorySaving ? "wait" : "pointer",
                    }}
                  >
                    {activeState.memorySaving ? "Sparar..." : "Spara endast MEMORY.md"}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(activeState.docsSaving)}
                    onClick={() => saveAgentDocs(activeAgent.id)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: "1px solid #111",
                      background: "#111",
                      color: "#fff",
                      cursor: activeState.docsSaving ? "wait" : "pointer",
                    }}
                  >
                    {activeState.docsSaving ? "Sparar..." : "Spara alla agentdokument"}
                  </button>
                  <button
                    type="button"
                    onClick={() => loadAgentDetails(activeAgent.id, { force: true })}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: "1px solid rgba(17,17,17,0.2)",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Ladda om agentdata
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

